import { type ReactNode, forwardRef, useRef } from "react";
import { cn } from "@/lib/utils";
import { AnimatedBeam } from "@/components/ui/animated-beam";
import { Logo } from "@/components/ui/logo";
import ProgressIcon from "@/components/home/ProgressIcon/ProgressIcon";
import { routines } from "@/components/home/ProgressAI/ProgressAI.data";

const Circle = forwardRef<HTMLDivElement, { className?: string; children?: ReactNode }>(
  ({ className, children }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "z-10 flex size-12 items-center justify-center rounded-full border-2 bg-white p-3 shadow-[0_0_20px_-12px_rgba(0,0,0,0.8)]",
          className,
        )}
      >
        {children}
      </div>
    );
  },
);

Circle.displayName = "Circle";

export function ProgressAI({
  className,
}: {
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const div1Ref = useRef<HTMLDivElement>(null);
  const div2Ref = useRef<HTMLDivElement>(null);
  const div3Ref = useRef<HTMLDivElement>(null);
  const div4Ref = useRef<HTMLDivElement>(null);
  const div5Ref = useRef<HTMLDivElement>(null);
  const div6Ref = useRef<HTMLDivElement>(null);
  const div7Ref = useRef<HTMLDivElement>(null);

  // Select 5 goals for the visualization
  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-blue-100 to-blue-200 dark:from-blue-800/20 dark:to-blue-900/30">
      <div
        className={cn(
          "flex-1 flex items-center justify-center overflow-hidden rounded-lg bg-white/30 dark:bg-gray-900/20 backdrop-blur-sm border border-indigo-200/30 dark:border-indigo-800/30",
          className,
        )}
        ref={containerRef}
      >
        <div className="flex size-full max-w-lg flex-row items-stretch justify-between gap-10 p-6">
          <div className="flex flex-col justify-center">
            <Circle ref={div7Ref}>
              <Icons.user />
            </Circle>
          </div>
          <div className="flex flex-col justify-center">
            <Circle ref={div6Ref} className="size-16 overflow-hidden">
              <Logo className="min-w-16 min-h-16" />
            </Circle>
          </div>
          <div className="flex flex-col justify-center gap-2">
            {routines.map((routine, index) => {
              const refMap = [div1Ref, div2Ref, div3Ref, div4Ref, div5Ref];
              return (
                <ProgressIcon
                  key={routine.id}
                  ref={refMap[index]}
                  type="step"
                  text={routine.text}
                  icon={routine.icon}
                  color={routine.color}
                  usePortal={false}
                />
              );
            })}
          </div>
        </div>

        {/* AnimatedBeams */}
        <AnimatedBeam containerRef={containerRef} fromRef={div1Ref} toRef={div6Ref} duration={3} />
        <AnimatedBeam containerRef={containerRef} fromRef={div2Ref} toRef={div6Ref} duration={3} />
        <AnimatedBeam containerRef={containerRef} fromRef={div3Ref} toRef={div6Ref} duration={3} />
        <AnimatedBeam containerRef={containerRef} fromRef={div4Ref} toRef={div6Ref} duration={3} />
        <AnimatedBeam containerRef={containerRef} fromRef={div5Ref} toRef={div6Ref} duration={3} />
        <AnimatedBeam containerRef={containerRef} fromRef={div6Ref} toRef={div7Ref} duration={3} />
      </div>
    </div>
  );
}

const Icons = {
  user: () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#000000"
      strokeWidth="2"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>User</title>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
};
